import { useState, useRef, useEffect } from "react";
import { DOCUMENT_DROPZONE_ACCEPT } from "shared/constants/officeFiles";
import { DeleteDesignerUploadService } from "shared/services/revision";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { Icons } from "assets";
import classNames from "classnames";
import CustomUpload, {
  UploadSortValue,
} from "shared/components/common/customUpload";
import styles from "./style.module.scss";
import ConfirmationModal from "shared/modal/confirmation";

interface FileUploadSectionProps {
  uploadKey: number;
  uploadingFiles: boolean;
  uploadProgress: { [key: string]: number };
  showUploadSuccess: boolean;
  currentFiles: File[];
  fileIdMap: Map<string, number>;
  uploadedFileIds: number[];
  processingFilesRef: React.MutableRefObject<string>;
  getFileKey: (file: File) => string;
  handleFilesSelected: (files: File[], isAddingMore: boolean) => Promise<void>;
  setUploadedFileIds: (ids: number[]) => void;
  setCurrentFiles: (files: File[]) => void;
  setFileIdMap: (map: Map<string, number>) => void;
  setShowUploadSuccess: (show: boolean) => void;
  mediaSort: UploadSortValue | null;
  onMediaSortChange: (value: UploadSortValue | null) => void;
  /** Widen the accepted types for a specific flow; defaults to the usual documents. */
  accept?: Record<string, string[]>;
}

export const FileUploadSection = ({
  uploadKey,
  uploadingFiles,
  uploadProgress,
  showUploadSuccess,
  currentFiles,
  fileIdMap,
  uploadedFileIds,
  processingFilesRef,
  getFileKey,
  handleFilesSelected,
  setUploadedFileIds,
  setCurrentFiles,
  setFileIdMap,
  setShowUploadSuccess,
  mediaSort,
  onMediaSortChange,
  accept,
}: FileUploadSectionProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState<File[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const pendingRestoreRef = useRef<File[] | null>(null);
  const isRestoringRef = useRef(false);
  const isSyncingAfterDeleteRef = useRef(false);

  const areFilesSame = (files1: File[], files2: File[]) => {
    if (files1.length !== files2.length) return false;

    const keys1 = files1.map(getFileKey).sort();
    const keys2 = files2.map(getFileKey).sort();

    return keys1.every((key, index) => key === keys2[index]);
  };

  const isAddingMoreFiles = (newFiles: File[], existingFiles: File[]) => {
    if (existingFiles.length === 0) return false;
    if (newFiles.length <= existingFiles.length) return false;

    const newFileKeys = new Set(newFiles.map(getFileKey));
    return existingFiles.every((file) => newFileKeys.has(getFileKey(file)));
  };

  const handleFileReorder = (files: File[]) => {
    setCurrentFiles(files);

    const reorderedIds = files
      .map((file) => {
        return fileIdMap.get(getFileKey(file));
      })
      .filter((id) => id !== undefined) as number[];

    setUploadedFileIds(reorderedIds);
  };

  const handleDeleteConfirm = async () => {
    if (filesToDelete.length === 0) {
      setShowDeleteModal(false);
      return;
    }

    setIsDeleting(true);

    let successCount = 0;
    let failCount = 0;

    try {
      for (const file of filesToDelete) {
        const fileKey = getFileKey(file);
        const uploadedId = fileIdMap.get(fileKey);

        if (uploadedId) {
          try {
            await DeleteDesignerUploadService(uploadedId);
            successCount++;
          } catch (error: any) {
            console.error(`Failed to delete file from S3: ${file.name}`, error);
            failCount++;
          }
        }
      }

      if (successCount > 0 && failCount === 0) {
        toastMessage(
          "success",
          `Successfully deleted ${successCount} file${
            successCount > 1 ? "s" : ""
          }`,
        );
      } else if (successCount > 0 && failCount > 0) {
        toastMessage(
          "warning",
          `Deleted ${successCount} file${
            successCount > 1 ? "s" : ""
          }, ${failCount} failed`,
        );
      } else if (failCount > 0) {
        toastMessage(
          "error",
          `Failed to delete ${failCount} file${failCount > 1 ? "s" : ""}`,
        );
      }

      if (successCount > 0) {
        const updatedFileIdMap = new Map(fileIdMap);
        filesToDelete.forEach((file) => {
          updatedFileIdMap.delete(getFileKey(file));
        });

        const remainingFiles = currentFiles.filter(
          (file) =>
            !filesToDelete.some((f) => getFileKey(f) === getFileKey(file)),
        );

        const remainingIds = remainingFiles
          .map((file) => fileIdMap.get(getFileKey(file)))
          .filter((id) => id !== undefined) as number[];

        setFileIdMap(updatedFileIdMap);
        setUploadedFileIds(remainingIds);

        isSyncingAfterDeleteRef.current = true;

        setCurrentFiles(remainingFiles);

        setTimeout(() => {
          isSyncingAfterDeleteRef.current = false;
        }, 200);

        if (remainingIds.length === 0) {
          setShowUploadSuccess(false);
        }
      } else {
        handleErrors(new Error("Failed to delete files"));
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setFilesToDelete([]);
    }
  };

  const handleFileRemoval = (removedFiles: File[]) => {
    if (removedFiles.length === 0) return;
    pendingRestoreRef.current = [...currentFiles];
    setFilesToDelete(removedFiles);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    if (pendingRestoreRef.current && !showDeleteModal && !isDeleting) {
      const filesToRestore = pendingRestoreRef.current;
      setTimeout(() => {
        isRestoringRef.current = true;
        const dataTransfer = new DataTransfer();
        filesToRestore.forEach((file) => {
          dataTransfer.items.add(file);
        });
      }, 100);
    }
  }, [showDeleteModal, isDeleting, currentFiles]);

  return (
    <>
      <div className="w-full">
        <CustomUpload
          key={uploadKey}
          accept={accept ?? DOCUMENT_DROPZONE_ACCEPT}
          externalProgress={uploadProgress}
          isUploading={uploadingFiles}
          syncFiles={isSyncingAfterDeleteRef.current ? currentFiles : undefined}
          existingFiles={currentFiles}
          getFileKey={getFileKey}
          mediaSort={mediaSort}
          onMediaSortChange={onMediaSortChange}
          onDuplicateError={(duplicateNames) => {
            const uniqueNames = Array.from(new Set(duplicateNames));
            toastMessage(
              "error",
              `File${uniqueNames.length > 1 ? "s" : ""} already uploaded: ${uniqueNames.join(", ")}`,
            );
          }}
          onFileChange={async (files) => {
            if (uploadingFiles) return;

            if (showDeleteModal) {
              return;
            }

            if (isSyncingAfterDeleteRef.current) {
              return;
            }

            if (files && files.length > 0) {
              const filesArray = Array.from(files);

              const filesSignature = filesArray
                .map(getFileKey)
                .sort()
                .join("|");

              if (processingFilesRef.current === filesSignature) {
                return;
              }

              if (
                currentFiles.length > 0 &&
                areFilesSame(filesArray, currentFiles)
              ) {
                handleFileReorder(filesArray);
                return;
              } else if (
                currentFiles.length > 0 &&
                isAddingMoreFiles(filesArray, currentFiles)
              ) {
                processingFilesRef.current = filesSignature;
                await handleFilesSelected(filesArray, true);
                setShowUploadSuccess(true);
                processingFilesRef.current = "";
                return;
              } else if (
                currentFiles.length > 0 &&
                filesArray.length < currentFiles.length
              ) {
                const newFileKeys = new Set(filesArray.map(getFileKey));
                const removedFiles = currentFiles.filter(
                  (file) => !newFileKeys.has(getFileKey(file)),
                );

                pendingRestoreRef.current = currentFiles;
                setFilesToDelete(removedFiles);
                setShowDeleteModal(true);
                return;
              } else {
                processingFilesRef.current = filesSignature;
                setShowUploadSuccess(false);
                setUploadedFileIds([]);
                setFileIdMap(new Map());
                await handleFilesSelected(filesArray, false);
                setShowUploadSuccess(true);
                processingFilesRef.current = "";
                return;
              }
            } else if (currentFiles.length > 0) {
              handleFileRemoval(currentFiles);
            }
          }}
        />
      </div>
      {showUploadSuccess && !uploadingFiles && (
        <div
          className={classNames(
            styles.uploadSuccessMessage,
            "flex gap-2 items-center",
          )}
        >
          <Icons.TickCircleIcon />
          <span>
            Files uploaded successfully. Don't forget to add a summary note
            before submission.
          </span>
        </div>
      )}
      <ConfirmationModal
        show={showDeleteModal}
        modalType="delete"
        title="Delete File?"
        subtitle={
          filesToDelete.length === 1
            ? `Are you sure you want to delete "${filesToDelete[0].name}"? This action cannot be undone.`
            : `Are you sure you want to delete ${filesToDelete.length} files? This action cannot be undone.`
        }
        actionButtonText="Delete"
        cancelButtonText="Cancel"
        loading={isDeleting}
        handleClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false);
            setFilesToDelete([]);
          }
        }}
        handleSubmit={handleDeleteConfirm}
      />
    </>
  );
};
